import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { loadSection } from "../../services/contentSource";
import testimonialsFallback from "../../data/home/testimonials.json";

const cleanReview = (rev: string) => {
  if (!rev) return "";
  return rev
    .replace(/Cognisun Team/gi, "MindSyncX Team")
    .replace(/Cognisun/gi, "MindSyncX")
    .replace(/this IT company/gi, "MindSyncX")
    .replace(/They were a pleasure to work wit$/gi, "they were an absolute pleasure to work with.")
    .replace(/knowledgeable, and always$/gi, "knowledgeable, and always proactive in anticipating our requirements.")
    .replace(/solutions that greatly$/gi, "solutions that greatly accelerated our digital roadmap.")
    .trim();
};

const cleanName = (item: any) => {
  if (item.name?.includes("Recognition Express")) return "Recognition Express";
  if (item.name?.includes("Day Lewis Pharmacy")) return "Collin Kendrick";
  return item.name;
};

const cleanRole = (item: any) => {
  if (item.name?.includes("Recognition Express")) return "Enterprise Client";
  if (item.name?.includes("Day Lewis Pharmacy")) return "Day Lewis Pharmacy";
  return item.designation || "Enterprise Partner";
};

interface TestimonialSectionProps {
  badgeText?: string;
}

const TestimonialSection: React.FC<TestimonialSectionProps> = ({
  badgeText = "05. VERIFIED CLIENT IMPACT",
}) => {
  const [data, setData] = useState<any>(testimonialsFallback);

  useEffect(() => {
    loadSection("testimonials", testimonialsFallback as any)
      .then(({ data: main }) => {
        if (main) setData(main);
      })
      .catch((err) => {
        console.warn("Using fallback testimonials data:", err);
      });
  }, []);

  if (!data) return null;

  return (
    <section className="testimonial-area mx-testimonials-revamp" id="testimonials">
      <div className="container mx-container">
        <div className="mx-testimonials-header-row">
          <div className="mx-testimonials-header-left">
            <div className="mx-hero-badge">
              <span className="mx-hero-badge-icon">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
              </span>
              <span>{badgeText}</span>
            </div>

            <h2 className="mx-testimonials-title">
              CLIENT SUCCESS STORIES
            </h2>

            <p className="mx-testimonials-lede">
              {data.subtitle || "Real impact, real results — explore how we turn bold ideas into AI-driven success stories."}
            </p>
          </div>

          <div className="mx-testimonials-header-right">
            <Link to={data.buttonLink || "/about"} className="mx-hero-btn-primary">
              <span>{(data.buttonText || "Explore Client Stories").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</span>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </Link>
          </div>
        </div>

        <div className="mx-testimonials-grid">
          {(data.testimonials || []).map((item: any, index: number) => {
            const ratingNum = parseFloat(item.rating) || 5.0;
            const reviewText = cleanReview(item.review);
            const authorName = cleanName(item);
            const authorRole = cleanRole(item);

            return (
              <div className="mx-testimonial-card" key={index}>
                <div className="mx-testimonial-top">
                  <div className="mx-testimonial-stars">
                    {[...Array(5)].map((_, starIndex) => (
                      <svg
                        key={starIndex}
                        className={`mx-star-icon ${starIndex < Math.round(ratingNum) ? "mx-star-filled" : ""}`}
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                      </svg>
                    ))}
                    <span className="mx-rating-score">{item.rating} / 5.0</span>
                  </div>

                  <div className="mx-quote-mark" aria-hidden="true">
                    “
                  </div>
                </div>

                <p className="mx-testimonial-review">
                  "{reviewText}"
                </p>

                <div className="mx-testimonial-author">
                  <div className="mx-author-avatar-wrap">
                    <img
                      src={item.avatar}
                      alt={authorName}
                      className="mx-author-avatar"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="mx-author-meta">
                    <h3 className="mx-author-name">{authorName}</h3>
                    <span className="mx-author-role">{authorRole}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
